import { convertHtmlStringValueToPlainTextLines } from '@AppModels/UiUtils';
import { IRichContentDocumentEditorPlaceholder } from '../../../../models';
import { Token } from '../../../../models/Token';

export interface IParsePastedContentOptions {
	preserveTopLevelLineBreaks?: boolean;
}

const escapeHtml = (value: string) =>
	value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * Reduces pasted markup to the plain text a text message can actually carry, keeping line breaks as
 * `<br>` because they are the one piece of structure that survives the wire.
 *
 * Placeholder chips render their friendly label ("first name") rather than their symbol, so
 * flattening them directly would leave literal prose that sends unpersonalized. Each chip is reduced
 * back to `{{its symbol}}` first, which the placeholders plugin rebuilds into a chip on insert.
 */
export const convertPastedContentToPlainTextHtml = (
	content: string,
	placeholders?: IRichContentDocumentEditorPlaceholder[]
) => {
	let withPlaceholderSymbols = content;

	if (placeholders?.length) {
		withPlaceholderSymbols = Token.replaceImpostorPlaceholders(withPlaceholderSymbols, placeholders);
		withPlaceholderSymbols = placeholders.reduce(
			(result, placeholder) => Token.replaceToken(result, placeholder, placeholder.symbol),
			withPlaceholderSymbols
		);
	}

	return escapeHtml(convertHtmlStringValueToPlainTextLines(withPlaceholderSymbols)).split('\n').join('<br />');
};

export const parseElement = (el: HTMLElement, domLvl: number, options?: IParsePastedContentOptions) => {
	if (domLvl === 1 && el.tagName === 'BR') {
		if (!options?.preserveTopLevelLineBreaks) {
			// remove extra spacing from Google Docs generated HTML
			el.parentNode.removeChild(el);
		}
		return;
	}
	if (!el.style.fontFamily || el.style.fontFamily.toLowerCase().includes('arial')) {
		el.style.fontFamily = 'arial,sans-serif';
		el.style.fontSize = '14px';
	}

	if (el.tagName === 'P') {
		const spans = el.querySelectorAll('span');
		if (spans.length === 1 && el.children.length === 1 && spans[0].innerHTML === '&nbsp;') {
			// remove extra spacing from Microsoft Word generated HTML
			el.parentNode.removeChild(el);
		} else {
			// ensure unnecessary spacing is not added from paragraph tags from Google Docs generated HTML
			el.style.margin = '0';
			el.style.padding = '0';
		}
	}

	if (el.tagName === 'UL' || el.tagName === 'OL') {
		el.style.margin = '0';
		el.style.padding = '0 0 0 40px';
	}

	if (el.children.length) {
		Array.from(el.children as HTMLCollectionOf<HTMLElement>).forEach(child => {
			parseElement(child, domLvl + 1, options);
		});
	}
};

export let surveyBaseUrls = [import.meta.env.VITE_SURVEYS_URL];

if (!import.meta.env.PROD) {
	surveyBaseUrls = surveyBaseUrls.concat([
		'https://localhost:3000/surveys.html',
		'https://surveys-staging.levitate.ai',
		'https://surveys-test.levitate.ai',
		'https://surveys.levitate.ai',
	]);
}

export let shortLinkBaseUrls = [import.meta.env.VITE_SHORTLINK_URL];

if (!import.meta.env.PROD) {
	shortLinkBaseUrls = shortLinkBaseUrls.concat([
		'https://localhost:5002',
		'https://go.staging.levitate.ai',
		'https://go.test.levitate.ai',
		'https://go.levitate.ai',
	]);
}

export const linkifySurveyLinks = (pastedText: string) => {
	const trimmedPastedContent = pastedText.trim() || '';
	const trimmedLowerContent = trimmedPastedContent.toLocaleLowerCase();

	if (!trimmedLowerContent) {
		return pastedText;
	}

	let end = trimmedPastedContent.length;

	// Convert the base URLs arrays to lowercase for case-insensitive comparison
	const lowerShortLinkBaseUrls = shortLinkBaseUrls.map(url => url?.toLowerCase()).filter(Boolean);
	const lowerSurveyBaseUrls = surveyBaseUrls.map(url => url?.toLowerCase()).filter(Boolean);

	// Check for short link URLs
	if (lowerShortLinkBaseUrls.some(x => x && trimmedLowerContent.startsWith(x))) {
		const matchIndex = /(\s|<|&lt;)/gim.exec(trimmedPastedContent)?.index || -1;
		end = matchIndex < 0 ? trimmedPastedContent.length : matchIndex;
		const remainder = trimmedPastedContent.substring(end);

		if (trimmedLowerContent.includes('source=customsurvey')) {
			return `<a target="_blank" href="${trimmedPastedContent.substring(0, end)}">${'Click here to take the custom survey'}</a>${remainder || ''}`;
		}

		if (trimmedLowerContent.includes('source=survey')) {
			return `<a target="_blank" href="${trimmedPastedContent.substring(0, end)}">${'Click here to take the survey'}</a>${remainder || ''}`;
		}
		if (trimmedLowerContent.includes('source=event')) {
			return `<a target="_blank" href="${trimmedPastedContent.substring(0, end)}">${'Click here to RSVP'}</a>${remainder || ''}`;
		}
		if (trimmedLowerContent.includes('source=donationsurvey')) {
			return `<a target="_blank" href="${trimmedPastedContent.substring(0, end)}">${'Click here to donate'}</a>${remainder || ''}`;
		}
	}

	// Check for direct survey URLs
	if (lowerSurveyBaseUrls.some(x => x && trimmedLowerContent.startsWith(x))) {
		const matchIndex = /(\s|<|&lt;)/gim.exec(trimmedPastedContent)?.index || -1;
		end = matchIndex < 0 ? trimmedPastedContent.length : matchIndex;
		const remainder = trimmedPastedContent.substring(end);

		return `<a target="_blank" href="${trimmedPastedContent.substring(0, end)}">${'Click here to take the survey'}</a>${remainder || ''}`;
	}

	return pastedText;
};
